/* Northsheep — colour siblings: swap the product page to another colour in place.
 *
 * Each colour is its own product (ns-colour-siblings.liquid). A tap fetches the
 * other colour's page and hands it to Dawn's own product swap
 * (product-info.js → handleSwapProduct), the same path Shopify's combined
 * listings use: <main> is replaced, the URL and title follow.
 *
 * The chosen size is kept when the other colour has it in stock
 * (data-sizes: variant title → variant id).
 *
 * Anything unexpected — no product-info, a failed fetch, a page without the
 * product section — falls back to following the link, so the pill always works.
 * Delegated on document: the swap replaces this very row.
 */
(() => {
  if (window.nsColourSiblingsReady) return;
  window.nsColourSiblingsReady = true;

  let controller = null;

  const selectedTitle = (info) => {
    const node = info.querySelector('variant-selects [data-selected-variant]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent)?.title ?? null;
    } catch (e) {
      return null;
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-ns-sib]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (link.getAttribute('aria-current') === 'true') {
      event.preventDefault();
      return;
    }

    const row = link.closest('[data-ns-sibs]');
    const info = link.closest('product-info');
    if (!row || row.dataset.swap !== '1') return;
    /* HTMLUpdateUtility is a `class` declaration in global.js. A class binding
       lives in the global LEXICAL scope and never becomes a property of
       window, so `window.HTMLUpdateUtility` is undefined even though the class
       is perfectly available — this guard silently sent every pill through a
       full page load, which is why Back had to be pressed once per colour
       looked at. `typeof` reads the binding itself and throws nothing when it
       really is missing. */
    if (!info || typeof info.handleSwapProduct !== 'function') return;
    if (typeof HTMLUpdateUtility === 'undefined') return;

    event.preventDefault();

    const path = link.dataset.path || link.pathname;
    let url = path;
    const title = selectedTitle(info);
    if (title && link.dataset.sizes) {
      try {
        const id = JSON.parse(link.dataset.sizes)[title];
        if (id) url += (url.includes('?') ? '&' : '?') + 'variant=' + id;
      } catch (e) {
        /* keep the plain product URL */
      }
    }

    controller?.abort();
    controller = new AbortController();
    row.setAttribute('aria-busy', 'true');
    link.classList.add('ns-sib--loading');
    const linkId = link.id;

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('ns-sib: ' + response.status);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        if (!html.querySelector("product-info[id^='MainProduct']") || !html.querySelector('main')) {
          throw new Error('ns-sib: no product section');
        }
        /* STAY WHERE THE SHOPPER IS (owner, 24 Sep 2026: "i just opened a
           brown sheepskin, then i clicked on mufflon and it jumped me down to
           recently viewed"). Dawn's swap inserts the new <main> ABOVE the old
           one and hides the old one a statement later; the new page's inline
           scripts run on insertion and measure things, and that layout lets
           Chrome's scroll anchoring hold the OLD page still - pushing the view
           down by a whole page height. Measured on desktop: 320 -> 6591, into
           the recently viewed row. No script scrolls; the browser does. So
           anchoring is off for the swap, the position is put back if anything
           moved it, and anchoring returns once Dawn has removed the old <main>
           (it waits 500ms). */
        const docEl = document.documentElement;
        const keepY = window.pageYOffset || 0;
        const anchorWas = docEl.style.overflowAnchor;
        docEl.style.overflowAnchor = 'none';
        info.handleSwapProduct(path, true)(html);
        if (Math.abs((window.pageYOffset || 0) - keepY) > 2) window.scrollTo(0, keepY);
        setTimeout(() => {
          docEl.style.overflowAnchor = anchorWas;
        }, 700);
        /* THE FLOATING BAR LIVES OUTSIDE THIS SECTION (owner, 22 Sep 2026:
           "if i use the type/colour selector then the floating add to cart is
           stuck on the first product page i opened").

           handleSwapProduct replaces the product section and nothing else, and
           the mobile sticky bar is rendered from theme.liquid — so it kept the
           first product's title, photograph, price and size map. The fetched
           page already contains the correct bar; this takes it. The script
           that drives the bar then re-reads everything on the event below,
           because the swap has just detached every node it was holding. */
        const barNow = document.querySelector('.ns-sticky-atc');
        const barNew = html.querySelector('.ns-sticky-atc');
        if (barNow && barNew) barNow.innerHTML = barNew.innerHTML;
        document.getElementById(linkId)?.focus({ preventScroll: true });
        // Cards in the new page (recommendations) need their swatch rows fitted.
        window.nsFitSwatches?.();
        document.dispatchEvent(new CustomEvent('ns:colour-swapped', { detail: { url } }));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        window.location.href = link.href;
      });
  });

  /* THE FAMILY FOLLOWS THE COLOUR (owner, 5 Oct 2026: "if i select beige colour
     i see the beige sizes also in beige thumbnail"). A row marked data-ns-follow
     is drawn for the colour on show (ns-colour-siblings.liquid): each size in
     that colour, a size whose colour is a product of its own leading there, a
     size without the colour left out. A colour picked on the page makes Dawn
     fetch the section for that colour and redraw only the variant picker, and
     this row sits above it - so the row's pills are taken from that fetched
     page, and its hidden state with them. Every photo is decoded before the
     pills go in, all at once, so no circle stands empty while it loads; a pick
     made since wins over an older one still loading. The row element itself
     stays (its fitted column width, the delegated click above). */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const html = event && event.data && event.data.html;
      if (!html) return;
      document.querySelectorAll('[data-ns-sibs][data-ns-follow]').forEach((row) => {
        const freshRow = html.querySelector(`[data-ns-follow="${CSS.escape(row.dataset.nsFollow)}"]`);
        const fresh = freshRow && freshRow.querySelector('.ns-sibs__list');
        const list = row.querySelector('.ns-sibs__list');
        if (!fresh || !list) return;
        const token = (row.nsFollowToken = (row.nsFollowToken || 0) + 1);
        const next = document.importNode(fresh, true);
        const waits = [...next.querySelectorAll('img')].map((img) => {
          const el = document.createElement('img');
          el.loading = 'eager';
          for (const a of img.attributes) if (a.name !== 'loading') el.setAttribute(a.name, a.value);
          img.replaceWith(el);
          return el.decode ? el.decode().catch(() => {}) : Promise.resolve();
        });
        Promise.all(waits).then(() => {
          if (!row.isConnected || row.nsFollowToken !== token) return;
          list.replaceChildren(...next.childNodes);
          row.hidden = freshRow.hidden;
        });
      });
    });
  }
})();
